'use client';

import { type ElementType } from 'react';

import type { ResponsiveOption } from '../../types/responsiveOption';
import type { Column } from './Grid';

import {
  type PolymorphicComponent,
  type PolymorphicComponentProps,
} from '../../types/component';
import { resolveResponsiveProps } from '../../utils/resolveResponsiveProps';
import { tv } from '../../utils/tv';

interface GridItemBaseProps {
  /**
   * Defines the number of columns the GridItem takes up within the parent `Grid`.
   * Note: colSpan values should not exceed the column count of the parent `Grid` to avoid layout issues.
   */
  colSpan?: ResponsiveOption<Column>;
}

const gridItemClasses = {
  root: 'zds-grid-item-root',
} as const;

const gridItemStyles = tv({
  base: [gridItemClasses.root],
  variants: {
    colSpan: {
      1: 'col-span-1',
      2: 'col-span-2',
      3: 'col-span-3',
      4: 'col-span-4',
      5: 'col-span-5',
      6: 'col-span-6',
      7: 'col-span-7',
      8: 'col-span-8',
      9: 'col-span-9',
      10: 'col-span-10',
      11: 'col-span-11',
      12: 'col-span-12',
    },
    colSpanXs: {
      1: 'xs:col-span-1',
      2: 'xs:col-span-2',
      3: 'xs:col-span-3',
      4: 'xs:col-span-4',
      5: 'xs:col-span-5',
      6: 'xs:col-span-6',
      7: 'xs:col-span-7',
      8: 'xs:col-span-8',
      9: 'xs:col-span-9',
      10: 'xs:col-span-10',
      11: 'xs:col-span-11',
      12: 'xs:col-span-12',
    },
    colSpanSm: {
      1: 'sm:col-span-1',
      2: 'sm:col-span-2',
      3: 'sm:col-span-3',
      4: 'sm:col-span-4',
      5: 'sm:col-span-5',
      6: 'sm:col-span-6',
      7: 'sm:col-span-7',
      8: 'sm:col-span-8',
      9: 'sm:col-span-9',
      10: 'sm:col-span-10',
      11: 'sm:col-span-11',
      12: 'sm:col-span-12',
    },
    colSpanMd: {
      1: 'md:col-span-1',
      2: 'md:col-span-2',
      3: 'md:col-span-3',
      4: 'md:col-span-4',
      5: 'md:col-span-5',
      6: 'md:col-span-6',
      7: 'md:col-span-7',
      8: 'md:col-span-8',
      9: 'md:col-span-9',
      10: 'md:col-span-10',
      11: 'md:col-span-11',
      12: 'md:col-span-12',
    },
    colSpanLg: {
      1: 'lg:col-span-1',
      2: 'lg:col-span-2',
      3: 'lg:col-span-3',
      4: 'lg:col-span-4',
      5: 'lg:col-span-5',
      6: 'lg:col-span-6',
      7: 'lg:col-span-7',
      8: 'lg:col-span-8',
      9: 'lg:col-span-9',
      10: 'lg:col-span-10',
      11: 'lg:col-span-11',
      12: 'lg:col-span-12',
    },
    colSpanXl: {
      1: 'xl:col-span-1',
      2: 'xl:col-span-2',
      3: 'xl:col-span-3',
      4: 'xl:col-span-4',
      5: 'xl:col-span-5',
      6: 'xl:col-span-6',
      7: 'xl:col-span-7',
      8: 'xl:col-span-8',
      9: 'xl:col-span-9',
      10: 'xl:col-span-10',
      11: 'xl:col-span-11',
      12: 'xl:col-span-12',
    },
  },
});

type GridItemProps<RootComponent extends ElementType = 'div'> =
  PolymorphicComponentProps<GridItemBaseProps, RootComponent>;

type GridItemComponent = PolymorphicComponent<GridItemBaseProps, 'div'>;

const GridItem: GridItemComponent = (props) => {
  const {
    component: Root = 'div',
    className,
    colSpan = 1,
    ref,
    ...rest
  } = props;

  const { defaultOption, responsiveOption } = resolveResponsiveProps(colSpan);

  const styles = gridItemStyles({
    colSpan: defaultOption,
    colSpanXs: responsiveOption?.xs,
    colSpanSm: responsiveOption?.sm,
    colSpanMd: responsiveOption?.md,
    colSpanLg: responsiveOption?.lg,
    colSpanXl: responsiveOption?.xl,
    className,
  });

  return <Root ref={ref} className={styles} {...rest} />;
};

GridItem.displayName = 'Grid.Item';

export { GridItem, type GridItemProps, type GridItemBaseProps, gridItemClasses };
